import { useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { apiFetch } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Lockup } from "../components/Lockup";
import { PasswordField } from "../components/PasswordField";

// Full staff 2FA login. email+password → second factor → session. Admin is SMS-primary with TOTP
// backup; the SMS leg is BUILT but unverified (Twilio A2P in review — no live SMS on any traffic).
// The TOTP leg is the verified path. Forced first-login password rotation is a real screen here,
// as is forced authenticator enrolment (the server no longer seeds a TOTP secret).
export default function Login() {
  const { setSession, reason, clearReason } = useAuth();
  const [step, setStep] = useState("credentials"); // credentials | second_factor | rotate | enroll
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [pwHide, setPwHide] = useState(0);   // bumped on submit: the field goes back to hidden
  const [primary, setPrimary] = useState("totp"); // sms | totp
  const [backups, setBackups] = useState([]);
  const [useBackup, setUseBackup] = useState(false);
  const [useRecovery, setUseRecovery] = useState(false);
  const [challengeId, setChallengeId] = useState(null);
  const [code, setCode] = useState("");
  const [rotateToken, setRotateToken] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [enrolToken, setEnrolToken] = useState(null);
  const [otpauthUri, setOtpauthUri] = useState("");
  const [enrolCode, setEnrolCode] = useState("");
  const [enrolDone, setEnrolDone] = useState(false);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const go = async (fn) => {
    setErr("");
    setBusy(true);
    try {
      await fn();
    } catch (e) {
      setErr(e.detail || "Something failed — please try again.");
    } finally {
      setBusy(false);
    }
  };

  const beginEnrolment = (token) =>
    go(async () => {
      const r = await apiFetch("/identity/staff/totp/begin", {
        method: "POST",
        body: { enrolment_token: token },
      });
      setEnrolToken(token);
      setOtpauthUri(r.otpauth_uri);
      setEnrolCode("");
      setEnrolDone(false);
      setStep("enroll");
    });

  const submitCredentials = () => {
    setPwHide((n) => n + 1);
    return go(async () => {
      clearReason();
      const r = await apiFetch("/identity/staff/login/start", {
        method: "POST",
        body: { email, password },
      });
      if (r.requires_totp_enrolment) {
        await beginEnrolment(r.enrolment_token);
        return;
      }
      setPrimary(r.method);
      setBackups(r.backup || []);
      setChallengeId(r.challenge_id || null);
      setUseBackup(false);
      setUseRecovery(false);
      setStep("second_factor");
    });
  };

  const handleSession = (r) => {
    if (r.requires_password_change) {
      setRotateToken(r.password_change_token);
      setStep("rotate");
    } else {
      setSession(r);
    }
  };

  const submitEnrolment = () =>
    go(async () => {
      await apiFetch("/identity/staff/totp/confirm", {
        method: "POST",
        body: { enrolment_token: enrolToken, code: enrolCode },
      });
      // Enrolment issues no session by design — return to sign-in to authenticate normally.
      setEnrolDone(true);
      setOtpauthUri("");
      setEnrolToken(null);
    });

  const submitSecondFactor = () =>
    go(async () => {
      if (useRecovery) {
        const r = await apiFetch("/identity/staff/login/recovery-code", {
          method: "POST", body: { email, code },
        });
        handleSession(r);
        return;
      }
      const onTotp = primary === "totp" || useBackup;
      const r = onTotp
        ? await apiFetch("/identity/staff/login/verify-totp", { method: "POST", body: { email, code } })
        : await apiFetch("/identity/staff/login/verify-sms", {
            method: "POST",
            body: { email, challenge_id: challengeId, code },
          });
      handleSession(r);
    });

  const submitRotation = () => {
    setPwHide((n) => n + 1);
    return go(async () => {
      if (newPassword !== confirmPassword) throw { detail: "New passwords do not match." };
      await apiFetch("/identity/staff/complete-password-change", {
        method: "POST",
        body: { token: rotateToken, new_password: newPassword },
      });
      // Rotation invalidates the flow — return to credentials to sign in with the new password.
      setStep("credentials");
      setPassword("");
      setCode("");
      setErr("");
      clearReason();
      setRotateToken(null);
    });
  };

  const onTotpNow = primary === "totp" || useBackup;

  return (
    <div className="login-wrap" data-testid="login-page">
      <div className="login-card">
        <div className="brand">
          <Lockup size="pre-account" />
          <div className="brand-sub">Juno's staff console</div>
        </div>

        {reason ? <div className="banner" data-testid="session-reason">{reason}</div> : null}
        {err ? <div className="login-error" data-testid="login-error">{err}</div> : null}

        {step === "credentials" && (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              submitCredentials();
            }}
          >
            <label>Email</label>
            <input data-testid="login-email" value={email} onChange={(e) => setEmail(e.target.value)}
                   autoComplete="username" type="email" required />
            <label>Password</label>
            <PasswordField testid="login-password" value={password} hideOn={pwHide}
                   onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" required />
            <button data-testid="login-continue" disabled={busy} type="submit">
              {busy ? "Checking…" : "Continue"}
            </button>
          </form>
        )}

        {step === "second_factor" && (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              submitSecondFactor();
            }}
          >
            <div className="step-note">
              {useRecovery
                ? "Enter a one-time break-glass recovery code. This uses up the code and raises an alert."
                : onTotpNow
                ? "Enter the 6-digit code from your authenticator app."
                : "Enter the code sent to your phone by SMS."}
            </div>
            {!onTotpNow && !useRecovery && (
              <div className="unverified-note" data-testid="sms-unverified">
                SMS delivery is pending carrier approval (A2P in review).
              </div>
            )}
            <label>{useRecovery ? "Recovery code" : onTotpNow ? "Authenticator code" : "SMS code"}</label>
            <input data-testid="login-code" value={code} onChange={(e) => setCode(e.target.value)}
                   inputMode={useRecovery ? "text" : "numeric"} autoComplete="one-time-code" required />
            <button data-testid="login-verify" disabled={busy} type="submit">
              {busy ? "Verifying…" : "Verify"}
            </button>
            {useRecovery && (
              <div className="unverified-note" data-testid="recovery-warning">
                One-time use — this code is consumed and raises a security alert.
              </div>
            )}
            {primary === "sms" && !useBackup && !useRecovery && (
              <button type="button" className="linkbtn" data-testid="use-totp-backup"
                      onClick={() => { setUseBackup(true); setCode(""); setErr(""); }}>
                Use authenticator app instead
              </button>
            )}
            {backups.includes("recovery_code") && !useRecovery && (
              <button type="button" className="linkbtn" data-testid="use-recovery-code"
                      onClick={() => { setUseRecovery(true); setCode(""); setErr(""); }}>
                Use a recovery code
              </button>
            )}
            {useRecovery && (
              <button type="button" className="linkbtn" data-testid="back-to-code"
                      onClick={() => { setUseRecovery(false); setCode(""); setErr(""); }}>
                Back to authenticator
              </button>
            )}
            <button type="button" className="linkbtn"
                    onClick={() => { setStep("credentials"); setCode(""); setUseRecovery(false); }}>
              Back to sign-in
            </button>
          </form>
        )}

        {step === "enroll" && (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (enrolDone) {
                setStep("credentials");
                setPassword("");
                setCode("");
                setEnrolCode("");
                setEnrolDone(false);
                clearReason();
              } else {
                submitEnrolment();
              }
            }}
            data-testid="enroll-form"
          >
            {!enrolDone ? (
              <>
                <div className="step-note">
                  Set up your authenticator app. Scan this QR code in Google Authenticator, Authy
                  or 1Password, then enter the 6-digit code it shows.
                </div>
                {otpauthUri ? (
                  <div className="qr-wrap" data-testid="enroll-qr">
                    <QRCodeSVG value={otpauthUri} size={200} level="M"
                               title="Authenticator setup QR code" />
                  </div>
                ) : null}
                <label>Authenticator code</label>
                <input data-testid="enroll-code" value={enrolCode}
                       onChange={(e) => setEnrolCode(e.target.value.replace(/\D/g, ""))}
                       inputMode="numeric" autoComplete="one-time-code" maxLength={6} required />
                <button data-testid="enroll-confirm" disabled={busy || enrolCode.length !== 6}
                        type="submit">
                  {busy ? "Confirming…" : "Confirm & finish setup"}
                </button>
                <div className="enroll-note">
                  This device's authenticator becomes your second factor. Keep it safe — it is
                  required at every sign-in.
                </div>
              </>
            ) : (
              <>
                <div className="step-note" data-testid="enroll-done">
                  Authenticator enrolled. Sign in again to continue.
                </div>
                <button data-testid="enroll-return" type="submit">Back to sign-in</button>
              </>
            )}
          </form>
        )}

        {step === "rotate" && (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              submitRotation();
            }}
          >
            <div className="step-note">
              First sign-in requires a new password before continuing.
            </div>
            <label>New password</label>
            <PasswordField testid="rotate-new" value={newPassword} hideOn={pwHide}
                   onChange={(e) => setNewPassword(e.target.value)} autoComplete="new-password" required />
            <label>Confirm new password</label>
            <PasswordField testid="rotate-confirm" value={confirmPassword} hideOn={pwHide}
                   onChange={(e) => setConfirmPassword(e.target.value)} autoComplete="new-password" required />
            <button data-testid="rotate-submit" disabled={busy} type="submit">
              {busy ? "Saving…" : "Set password & continue"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
